import { useEffect, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../lib/api';
import { useAuthStore } from '../lib/store';
import { Instagram, Facebook, Linkedin, TrendingUp, Calendar, Image } from 'lucide-react';

export default function Dashboard() {
  const { token } = useAuthStore();
  const [stats, setStats] = useState<any>(null);
  const [recentPosts, setRecentPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      api.setToken(token);
      loadDashboard();
    }
  }, [token]);

  const loadDashboard = async () => {
    try {
      const [postsData, analyticsData] = await Promise.all([
        api.getPosts(),
        api.getAnalyticsOverview(7),
      ]);

      setRecentPosts(postsData.slice(0, 5));
      setStats(analyticsData);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-2">Welcome back! Here's your overview.</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatCard
            icon={<TrendingUp className="w-6 h-6" />}
            label="Total Posts"
            value={recentPosts.length}
            color="blue"
          />
          <StatCard
            icon={<Calendar className="w-6 h-6" />}
            label="Scheduled"
            value={recentPosts.filter((p) => p.status === 'scheduled').length}
            color="purple"
          />
          <StatCard
            icon={<Image className="w-6 h-6" />}
            label="Published"
            value={recentPosts.filter((p) => p.status === 'published').length}
            color="green"
          />
          <StatCard
            icon={<TrendingUp className="w-6 h-6" />}
            label="Engagement"
            value="-"
            color="pink"
          />
        </div>

        {/* Quick Actions */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <QuickAction
              icon={<Instagram className="w-5 h-5" />}
              label="Instagram Post"
              onClick={() => {}}
            />
            <QuickAction
              icon={<Facebook className="w-5 h-5" />}
              label="Facebook Post"
              onClick={() => {}}
            />
            <QuickAction
              icon={<Linkedin className="w-5 h-5" />}
              label="LinkedIn Post"
              onClick={() => {}}
            />
            <QuickAction
              icon={<Image className="w-5 h-5" />}
              label="Generate Image"
              onClick={() => {}}
            />
          </div>
        </div>

        {/* Recent Posts */}
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Recent Posts</h2>
          {recentPosts.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No posts yet. Create your first post!</p>
          ) : (
            <div className="space-y-4">
              {recentPosts.map((post) => (
                <div key={post.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-gray-900">{post.platform}</span>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          post.status === 'published'
                            ? 'bg-green-100 text-green-700'
                            : post.status === 'scheduled'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {post.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2">{post.caption}</p>
                  </div>
                  <div className="text-sm text-gray-500">
                    {new Date(post.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function StatCard({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  color: string;
}) {
  const colorClasses = {
    blue: 'bg-blue-500',
    purple: 'bg-purple-500',
    green: 'bg-green-500',
    pink: 'bg-pink-500',
  }[color];

  return (
    <div className="card">
      <div className={`w-12 h-12 rounded-lg ${colorClasses} text-white flex items-center justify-center mb-4`}>
        {icon}
      </div>
      <p className="text-sm text-gray-600">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
    </div>
  );
}

function QuickAction({
  icon,
  label,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-2 p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
    >
      <div className="text-primary-600">{icon}</div>
      <span className="text-sm font-medium text-gray-700">{label}</span>
    </button>
  );
}
